import { Link } from 'react-router-dom';
import { getPublished } from '@/data/loader';
import { useI18n, useLocalizedPath } from '@/hooks/useI18n';
import { BaresSEO } from '@/components/SEO';
import { Container } from '@/components/Container';
import { OptimizedImage } from '@/components/OptimizedImage';
import { BaresQuiz } from '@/components/BaresQuiz';
import { AuthorBox } from '@/components/AuthorBox';
import { defaultAuthor } from '@/data/author';
// @ts-ignore
import baresData from '@/data/bares.json';

/* ─── Cross-link: Cavalo Marinho (é "Bar e Restaurante" mas vive em restaurantes.json) ─── */
const CAVALO = {
  handle: 'cavalo-marinho',
  title: 'Cavalo Marinho',
  blurb:
    'Um dos points mais animados de Encantadas: bar e restaurante no meio da trilha, com música ao vivo (reggae, forró, MPB, rock), a famosa Melancia Atômica e cozinha até mais tarde.',
  vilarejoName: 'Encantadas',
  tipoLabel: 'Bar e restaurante',
  musicaAoVivo: true,
  link: '/restaurante/cavalo-marinho',
};

/* ─── "Ideal para" derivado (não há campo próprio no JSON) ─── */
const IDEAL_PARA: Record<string, string> = {
  'madruga-burger-beer': 'Fome de burger com bom humor',
  'stazione-bar-e-restaurante': 'Jantar com pizza e música',
  'barranco-bar-e-restaurante': 'Fim de tarde com vista pro Farol',
  'forro-do-wando': 'Dançar forró ao vivo',
  'ilha-do-malte-beer-burguer': 'Cerveja artesanal e burger de costela',
  'cavalo-marinho': 'Curtir a noite com show e drinks',
};

type BarCardData = { title: string; blurb: string; link: string; chips: string[] };

function BarCard({ data }: { data: BarCardData }) {
  return (
    <Link to={data.link} className="block h-full">
      <div className="h-full bg-white rounded-2xl border border-sand-200 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md hover:border-teal-light/40 transition-all">
        <h3 className="font-display text-lg text-warm-dark leading-tight mb-2">{data.title}</h3>
        <p className="font-body text-sm text-warm-gray leading-relaxed mb-3">{data.blurb}</p>
        <div className="flex flex-wrap gap-1.5">
          {data.chips.map(c => (
            <span
              key={c}
              className="font-body text-[11px] bg-sand-100 text-warm-dark px-2 py-0.5 rounded-full"
            >
              {c}
            </span>
          ))}
          <span className="font-body text-[11px] text-teal-deep font-semibold ml-auto self-center">
            Ver →
          </span>
        </div>
      </div>
    </Link>
  );
}

/* ─── FAQ do pilar (também alimenta o schema em BaresSEO) ─── */
const FAQ: { q: string; a: string }[] = [
  {
    q: 'A Ilha do Mel tem balada?',
    a: 'Não no sentido de balada de cidade. A ilha é rústica, sem carros e sem grandes casas noturnas — o agito acontece em bares com música ao vivo, sobretudo em fins de semana e na alta temporada, divididos entre Encantadas e Nova Brasília.',
  },
  {
    q: 'Até que horas funciona a noite na ilha?',
    a: 'A música ao vivo é regulada: deve ser acústica, com limite por volta da meia-noite em dias de semana e até 2h em fins de semana e feriados. Fora disso, os bares seguem servindo, mas o movimento cai bem depois desse horário.',
  },
  {
    q: 'Tem música ao vivo? Que estilos?',
    a: 'Tem. O forró pé-de-serra reina no Forró do Wando, em Nova Brasília; o Cavalo Marinho e o Stazione, em Encantadas, alternam reggae, MPB, rock e karaokê. A programação é irregular fora da temporada — vale confirmar no dia.',
  },
  {
    q: 'Os bares aceitam cartão ou preciso levar dinheiro?',
    a: 'Leve dinheiro e/ou pix. Alguns aceitam cartão, mas os bares menores costumam não passar, e o sinal de internet oscila o tempo todo. Não há caixa eletrônico na ilha, então saque antes de atravessar.',
  },
  {
    q: 'É caro? Quanto custa a cerveja?',
    a: 'A cerveja gira em torno de R$25 (varia por casa e temporada). No geral, os preços da ilha são um pouco mais altos que no continente, porque tudo chega de barca. Petiscos e porções também.',
  },
  {
    q: 'Onde tem mais movimento à noite, Encantadas ou Nova Brasília?',
    a: 'As duas concentram a vida noturna, cada uma com seus points. Encantadas tem Cavalo Marinho, Stazione e as hamburguerias; Nova Brasília tem o forró e bares pé na areia. Como atravessar de vila à noite é difícil, o mais prático é curtir onde você dorme.',
  },
  {
    q: 'É seguro voltar à noite? Preciso de lanterna?',
    a: 'A ilha é tranquila, mas não há iluminação pública nas trilhas e caminhos de areia. Leve lanterna ou use a do celular — é indispensável para voltar ao alojamento no escuro com segurança.',
  },
  {
    q: 'Tem hamburgueria e cerveja artesanal?',
    a: 'Sim, em Encantadas. O Madruga Burger Beer é temático do Seu Madruga e bem avaliado; o Ilha do Malte foca em cervejas artesanais e no hambúrguer de costela. Boa alternativa a quem quer fugir dos frutos do mar.',
  },
  {
    q: 'Tem forró? Onde?',
    a: 'O forró ao vivo rola no Forró do Wando, em Nova Brasília, perto do Toca do Abutre — às vezes com bandas nativas da ilha. Não tem redes sociais nem horário fixo, então pergunte a moradores ou à sua pousada se vai ter forró na noite.',
  },
  {
    q: 'Funciona fora de temporada ou em dia de semana?',
    a: 'Sim, mas com muito menos movimento e programação. Fora da alta temporada e nos dias de semana, vários bares reduzem os shows e podem fechar mais cedo. Confirme antes de sair, principalmente no inverno.',
  },
];

export function BaresPage() {
  const { lang } = useI18n();
  const localize = useLocalizedPath();

  // Bares publicados (fallback para o JSON caso o loader ainda não conheça 'bar')
  const liveBares = getPublished('bar', lang);
  const bares: any[] = liveBares.length ? liveBares : (baresData as any[]);

  const barByHandle = (h: string) => bares.find(b => b.handle === h);
  const baresByTipo = (tipo: string) => bares.filter(b => b.tipo === tipo);

  // Linhas da tabela: 5 bares + Cavalo Marinho (cross-link)
  const tableRows = [
    ...bares.map(b => ({
      title: b.title,
      vila: b.vilarejoName,
      tipo: b.tipoLabel,
      musica: b.musicaAoVivo,
      idealPara: IDEAL_PARA[b.handle] || b.subtitle,
      link: `/bar/${b.handle}`,
    })),
    {
      title: CAVALO.title,
      vila: CAVALO.vilarejoName,
      tipo: CAVALO.tipoLabel,
      musica: CAVALO.musicaAoVivo,
      idealPara: IDEAL_PARA[CAVALO.handle],
      link: CAVALO.link,
    },
  ];

  // Bares por vila (para a seção "Por vila")
  const encantadas = bares.filter(b => b.vila === 'encantadas');
  const novaBrasilia = bares.filter(b => b.vila === 'nova-brasilia');

  return (
    <>
      <BaresSEO />
      <div>
        {/* Hero */}
        <div className="relative mb-10">
          <div className="relative h-56 sm:h-64">
            <OptimizedImage
              src="/images/gruta-encantadas-noite.jpg"
              alt="Noite na Ilha do Mel, com o clima rústico dos bares e da vida noturna da ilha"
              className="w-full h-full object-cover"
              priority
            />
            <div className="absolute inset-0 hero-gradient" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center text-center px-4">
            <div>
              <h1 className="font-display text-4xl sm:text-5xl text-white mb-2">
                Bares na Ilha do Mel: onde beber, comer e curtir a noite
              </h1>
              <p className="font-editorial text-lg sm:text-xl text-white/80">
                Onde tem música ao vivo, forró, hambúrguer e cerveja gelada — e o que esperar da
                vida noturna de uma ilha sem carro e sem balada
              </p>
            </div>
          </div>
        </div>

        <Container>
          {/* Intro honesta */}
          <div className="max-w-3xl mx-auto mb-12 space-y-4">
            <p className="font-editorial text-lg text-warm-gray leading-relaxed">
              Vamos ser honestos: a{' '}
              <Link to="/" className="text-teal-deep font-medium hover:underline">
                Ilha do Mel
              </Link>{' '}
              é rústica e <strong>não tem balada de verdade</strong>. Não espere clube, pista de
              dança gigante ou festa até o sol nascer. O que existe é um punhado de bares, alguns
              com música ao vivo, e um agito que se concentra nos fins de semana e na alta
              temporada.
            </p>
            <p className="font-body text-warm-gray leading-relaxed">
              Essa vida noturna se divide entre as duas vilas — <strong>Encantadas</strong> e{' '}
              <strong>Nova Brasília</strong> — e, como não há carros nem iluminação nos caminhos,
              o normal é curtir perto de onde se dorme. Abaixo você compara todos os bares lado a
              lado, faz um teste pra achar o seu e entende as regras da noite na ilha (horário da
              música, dinheiro na mão, lanterna no bolso).
            </p>
          </div>

          {/* TL;DR / resposta rápida */}
          <section className="mb-14" id="resumo">
            <div className="max-w-3xl mx-auto bg-teal-light/5 border border-teal-light/20 rounded-2xl p-6 sm:p-8">
              <span className="font-body text-sm text-teal-deep font-semibold uppercase tracking-wider">
                Resposta rápida
              </span>
              <h2 className="font-display text-2xl text-warm-dark mt-1 mb-4">
                A noite na Ilha do Mel em 4 respostas
              </h2>
              <dl className="space-y-3">
                {[
                  {
                    q: 'Tem balada?',
                    a: 'Não — é rústico, com bares e música ao vivo. O agito se concentra em fins de semana e na alta temporada.',
                  },
                  {
                    q: 'Até que horas?',
                    a: 'Música ao vivo até meia-noite (dia de semana) ou 2h (fim de semana e feriado).',
                  },
                  {
                    q: 'Aceita cartão?',
                    a: 'Leve dinheiro/pix — nem todos passam cartão e o sinal oscila. Não há caixa eletrônico na ilha.',
                  },
                  {
                    q: 'É caro?',
                    a: 'A cerveja fica em torno de R$25; varia por casa e temporada.',
                  },
                ].map(item => (
                  <div key={item.q} className="flex gap-3">
                    <dt className="font-display text-warm-dark text-sm sm:text-base flex-shrink-0 w-32 sm:w-40">
                      {item.q}
                    </dt>
                    <dd className="font-body text-sm text-warm-gray leading-relaxed">{item.a}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>

          {/* Tabela comparativa */}
          <section className="mb-16" id="comparativo">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">
              Os bares lado a lado
            </h2>
            <p className="font-editorial text-warm-gray mb-6">
              Vila, tipo, se tem música ao vivo e pra que cada casa é melhor — pra comparar de um
              golpe de vista.
            </p>
            <div className="overflow-x-auto rounded-2xl border border-sand-200">
              <table className="w-full text-left border-collapse min-w-[640px]">
                <thead>
                  <tr className="bg-sand-50 text-warm-dark">
                    <th className="font-display text-sm p-3">Bar</th>
                    <th className="font-display text-sm p-3">Vila</th>
                    <th className="font-display text-sm p-3">Tipo</th>
                    <th className="font-display text-sm p-3">Música ao vivo?</th>
                    <th className="font-display text-sm p-3">Ideal para</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((r, i) => (
                    <tr
                      key={r.title}
                      className={`border-t border-sand-100 ${i % 2 ? 'bg-sand-50/40' : 'bg-white'}`}
                    >
                      <td className="p-3 font-body text-sm text-warm-dark font-medium">
                        <Link to={r.link} className="hover:text-teal-deep hover:underline">
                          {r.title}
                        </Link>
                      </td>
                      <td className="p-3 font-body text-sm text-warm-gray">{r.vila}</td>
                      <td className="p-3 font-body text-sm text-warm-gray">{r.tipo}</td>
                      <td className="p-3 font-body text-sm text-warm-gray">
                        {r.musica ? 'Sim' : 'Não'}
                      </td>
                      <td className="p-3 font-body text-sm text-warm-gray">{r.idealPara}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="font-body text-xs text-warm-gray mt-2">
              Preços, horários e programação musical variam bastante por temporada — confirme no
              local antes de ir.
            </p>
          </section>

          {/* Quiz */}
          <BaresQuiz bares={bares} />

          {/* Por tipo — Bar e restaurante */}
          <section className="mb-16" id="tipo-bar-restaurante">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">
              Comer e beber no mesmo lugar
            </h2>
            <p className="font-editorial text-warm-gray mb-6 max-w-3xl">
              Casas que servem prato cheio e também funcionam como point de fim de tarde e de
              noite — de pizza com música a bar pé na areia.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <BarCard
                data={{
                  title: CAVALO.title,
                  blurb: CAVALO.blurb,
                  link: CAVALO.link,
                  chips: ['Encantadas', 'Música ao vivo'],
                }}
              />
              {baresByTipo('bar-restaurante').map(b => (
                <BarCard
                  key={b.handle}
                  data={{
                    title: b.title,
                    blurb: b.subtitle,
                    link: `/bar/${b.handle}`,
                    chips: [b.vilarejoName, b.musicaAoVivo ? 'Música ao vivo' : 'Vista / pé na areia'],
                  }}
                />
              ))}
            </div>
          </section>

          {/* Por tipo — Música ao vivo & forró */}
          <section className="mb-16" id="tipo-musica">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">
              Para ouvir música e dançar
            </h2>
            <p className="font-editorial text-warm-gray mb-6 max-w-3xl">
              Quem quer pé na dança encontra o forró pé-de-serra ao vivo em Nova Brasília — o
              point de agito mais tradicional da ilha.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {baresByTipo('musica-forro').map(b => (
                <BarCard
                  key={b.handle}
                  data={{
                    title: b.title,
                    blurb: b.subtitle,
                    link: `/bar/${b.handle}`,
                    chips: [b.vilarejoName, 'Forró ao vivo'],
                  }}
                />
              ))}
            </div>
          </section>

          {/* Por tipo — Hambúrguer & cerveja artesanal */}
          <section className="mb-16" id="tipo-hamburguer">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">
              Hambúrguer e cerveja artesanal
            </h2>
            <p className="font-editorial text-warm-gray mb-6 max-w-3xl">
              A alternativa aos frutos do mar fica em Encantadas: hamburgueria bem avaliada e casa
              de cerveja artesanal, pra matar a fome da noite sem formalidade.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {baresByTipo('hamburgueria-cerveja').map(b => (
                <BarCard
                  key={b.handle}
                  data={{
                    title: b.title,
                    blurb: b.subtitle,
                    link: `/bar/${b.handle}`,
                    chips: [b.vilarejoName, 'Hambúrguer & cerveja'],
                  }}
                />
              ))}
            </div>
          </section>

          {/* Por vila */}
          <section className="mb-16" id="por-vila">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Encantadas */}
              <div className="bg-white border border-sand-200 rounded-2xl p-6 sm:p-7">
                <h2 className="font-display text-2xl sm:text-3xl text-warm-dark mb-3">
                  À noite em Encantadas
                </h2>
                <ul className="space-y-2 mb-4">
                  <li className="font-body text-sm">
                    <Link
                      to={localize(CAVALO.link)}
                      className="text-teal-deep font-medium hover:underline"
                    >
                      Cavalo Marinho
                    </Link>{' '}
                    <span className="text-warm-gray">— música ao vivo e drinks, o mais festeiro</span>
                  </li>
                  {encantadas.map(b => (
                    <li key={b.handle} className="font-body text-sm">
                      <Link
                        to={localize(`/bar/${b.handle}`)}
                        className="text-teal-deep font-medium hover:underline"
                      >
                        {b.title}
                      </Link>{' '}
                      <span className="text-warm-gray">— {b.tipoLabel.toLowerCase()}</span>
                    </li>
                  ))}
                </ul>
                <p className="font-body text-xs text-warm-gray leading-relaxed border-t border-sand-100 pt-3">
                  Se você dorme em Encantadas, curta por aqui: atravessar para Nova Brasília à noite,
                  no escuro, é demorado e desconfortável.
                </p>
              </div>

              {/* Nova Brasília */}
              <div className="bg-white border border-sand-200 rounded-2xl p-6 sm:p-7">
                <h2 className="font-display text-2xl sm:text-3xl text-warm-dark mb-3">
                  À noite em Nova Brasília
                </h2>
                <ul className="space-y-2 mb-4">
                  {novaBrasilia.map(b => (
                    <li key={b.handle} className="font-body text-sm">
                      <Link
                        to={localize(`/bar/${b.handle}`)}
                        className="text-teal-deep font-medium hover:underline"
                      >
                        {b.title}
                      </Link>{' '}
                      <span className="text-warm-gray">— {b.tipoLabel.toLowerCase()}</span>
                    </li>
                  ))}
                </ul>
                <p className="font-body text-xs text-warm-gray leading-relaxed border-t border-sand-100 pt-3">
                  É o lado do forró e dos bares pé na areia. Como o deslocamento entre as vilas à
                  noite é difícil, o ideal é curtir na vila onde está hospedado.
                </p>
              </div>
            </div>
          </section>

          {/* Vida noturna na prática */}
          <section className="mb-16" id="na-pratica">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-6">
              Vida noturna na prática: o que saber antes
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {
                  icon: '💵',
                  t: 'Dinheiro em espécie',
                  d: 'Não há caixa eletrônico na ilha e muitos bares não passam cartão. Saque no continente e leve dinheiro e/ou pix — o sinal de internet oscila bastante.',
                },
                {
                  icon: '🔦',
                  t: 'Lanterna sempre',
                  d: 'Não existe iluminação pública nas trilhas e caminhos de areia. Leve uma lanterna (ou use a do celular) para voltar ao alojamento no escuro com segurança.',
                },
                {
                  icon: '🍺',
                  t: 'Quanto custa a cerveja',
                  d: 'A cerveja fica em torno de R$25 e varia por casa e temporada. Como tudo chega de barca, os preços tendem a ser um pouco mais altos que no continente.',
                },
                {
                  icon: '🎸',
                  t: 'A regra da música ao vivo',
                  d: 'A música ao vivo deve ser acústica, com limite por volta da meia-noite em dias de semana e até 2h em fins de semana e feriados. Depois disso, o movimento cai.',
                },
                {
                  icon: '🌕',
                  t: 'Luau e lua cheia',
                  d: 'Nas noites de lua cheia e em datas especiais rolam luaus na praia — encontros informais, com violão e fogueira. Pergunte a moradores e à sua pousada se vai ter algum na sua estadia.',
                },
                {
                  icon: '🗓️',
                  t: 'Depende da temporada',
                  d: 'Fora da alta temporada e em dias de semana, a programação diminui e vários bares fecham mais cedo. O agito é mais garantido nos fins de semana de verão.',
                },
              ].map(c => (
                <div
                  key={c.t}
                  className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-sand-200"
                >
                  <span className="text-2xl flex-shrink-0" aria-hidden="true">
                    {c.icon}
                  </span>
                  <div>
                    <h3 className="font-display text-lg text-warm-dark mb-1">{c.t}</h3>
                    <p className="font-body text-sm text-warm-gray leading-relaxed">{c.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16" id="faq">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-6">
              Perguntas frequentes
            </h2>
            <div className="space-y-3 max-w-3xl">
              {FAQ.map((f, i) => (
                <details
                  key={i}
                  className="group bg-white border border-sand-200 rounded-lg overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-sand-50 list-none">
                    <span className="font-display text-base text-warm-dark pr-4">{f.q}</span>
                    <span className="text-teal-deep flex-shrink-0 transition-transform duration-200 group-open:rotate-180">
                      ▼
                    </span>
                  </summary>
                  <div className="px-4 pb-4 font-body text-warm-gray text-sm leading-relaxed">
                    {f.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA + links internos */}
          <section className="mb-16 bg-teal-deep rounded-2xl p-8 lg:p-12 text-center">
            <h2 className="font-display text-3xl sm:text-4xl text-white mb-3">Monte o resto da noite</h2>
            <p className="font-editorial text-white/80 mb-6 max-w-xl mx-auto">
              Já sabe onde beber? Agora resolva onde dormir, onde jantar e o que fazer durante o
              dia na ilha.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to={localize('/pousadas-ilha-do-mel')}
                className="inline-flex items-center gap-2 bg-white text-teal-deep hover:bg-sand-100 font-body font-semibold rounded-xl px-5 py-3 transition-all"
              >
                Pousadas
              </Link>
              <Link
                to={localize('/restaurantes-ilha-do-mel')}
                className="inline-flex items-center gap-2 bg-white text-teal-deep hover:bg-sand-100 font-body font-semibold rounded-xl px-5 py-3 transition-all"
              >
                Restaurantes
              </Link>
              <Link
                to={localize('/o-que-fazer-ilha-do-mel')}
                className="inline-flex items-center gap-2 bg-white text-teal-deep hover:bg-sand-100 font-body font-semibold rounded-xl px-5 py-3 transition-all"
              >
                O que fazer
              </Link>
            </div>
          </section>

          {/* Autoria (E-E-A-T) */}
          <div className="max-w-3xl mx-auto">
            <AuthorBox author={defaultAuthor} />
          </div>
        </Container>
      </div>
    </>
  );
}
